import { JetView } from 'webix-jet'; // Importing JetView from the webix-jet library for creating views
import { allpayments } from 'models/allpayments'; // Importing allpayments model for data handling
import { devConfig } from 'config/config'; // Importing development configuration settings

/**
 * Class representing the commission report view.
 * @extends JetView
 */
export default class tableComissionReportView extends JetView {
    /**
     * Configures the view layout and components.
     * @returns {Object} The configuration object for the view.
     */
    config() {
        /**
         * Retrieves the localization service and date format configuration.
         */
        const _ = this.app.getService('locale')._;

        /**
         * Converts dates to a string format based on the application's date format configuration.
         */
        const dateFormat = webix.Date.dateToStr(this.app.config.dateFormat);

        /**
         * Retrieves the current user parameter from the application.
         */
        const cur_user = this.getParam('user', true);

        /**
         * Extends the Webix data filter to create a custom column summarizer for tree tables.
         * This summarizer calculates the total for a specific column and formats the result.
         */
        webix.ui.datafilter.summTreeColumn = webix.extend(
            {
                /**
                 * Refreshes the summary column with the calculated total.
                 * @param {Object} master - The master tree table object.
                 * @param {HTMLElement} node - The HTML node of the column footer.
                 * @param {Object} value - The configuration object for the column.
                 */
                refresh: function(master, node, value) {
                    var result = 0; // Stores the calculated total
                    var resultFormat = 0; // Stores the formatted total
                    master.data.each(function(obj) {
                        if (obj.$level == 1) result += obj[value.columnId]; // Sum values at level 1
                    });
                    if (value.columnId === 'CantidadTickets') {
                        resultFormat = webix.i18n.intFormat(result); // Format as an integer
                    } else {
                        resultFormat = webix.i18n.priceFormat(result); // Format as a price
                    }
                    node.firstChild.innerHTML = resultFormat; // Update the footer with the formatted total
                },
            },
            webix.ui.datafilter.summColumn, // Base Webix column summarizer
        );

        /**
         * Configuration object for the commission report tree table.
         * Defines the layout, columns, and behavior of the tree table.
         */
        return {
            view: 'treetable', // Tree table view
            id: 'treetable', // Unique identifier for the tree table
            css: 'webix_header_border webix_data_border webix_footer_border', // CSS classes for styling
            select: true, // Enable row selection
            columns: [
                {
                    id: 'TypeName', // Column ID
                    header: 'TypeName', // Column header
                    width: 200, // Column width
                    template: function(obj, common) {
                        // Template for rendering the column content
                        if (obj.$group) return common.treetable(obj, common) + obj.value;
                        return obj.TypeName;
                    },
                    footer: 'Total:', // Footer text
                },
                {
                    id: 'ActionTittle', // Column ID
                    header: '', // Empty header
                    width: 70, // Column width
                    height: 25, // Column height
                    tooltip: 'Acción', // Tooltip text
                    template: function(obj) {
                        // Template for rendering action buttons based on state
                        if (obj.State == 'A') {
                            if (devConfig.getPermAction('agentComissionsReportBetShopDetailApprove', 'Approve')) {
                                return '<span placeholder=\'Aprobar\' class=\'webix_icon wxi-close approve prm-agentComissionsReportBetShopDetailApprove\'></span><span class=\'webix_icon icon-history torefuse prm-agentComissionsReportBetShopDetailApprove\'></span><span placeholder=\'Pagar\' class=\'webix_icon wxi-close pay prm-agentComissionsReportBetShopDetailApprove\'></span>';
                            } else {
                                return '';
                            }
                        } else {
                            if (obj.State == 'P') {
                                if (devConfig.getPermAction('agentComissionsReportBetShopDetailPay', 'Pay')) {
                                    return '<span placeholder=\'Pagar\' class=\'webix_icon wxi-close pay prm-agentComissionsReportBetShopDetailPay\'></span>';
                                } else {
                                    return '';
                                }
                            } else {
                                return '';
                            }
                        }
                    },
                },
                { id: 'Id', header: 'ID', width: 70, sort: 'int' }, // ID column with sorting
                {
                    id: 'ClientId', // Column ID
                    header: _('ID Usuario'), // Localized header
                    width: 95, // Column width
                    sort: 'int', // Enable integer sorting
                },
                {
                    id: 'CreatedLocal', // Column ID
                    header: _('Fecha de Creación'), // Localized header
                    fillspace: 0, // No fill space
                    minWidth: 0, // Minimum width
                    width: 150, // Column width
                    sort: 'text', // Enable text sorting
                    format: dateFormat, // Date format
                },
                {
                    id: 'State', // Column ID
                    header: _('Estado'), // Localized header
                    options: [
                        { id: '', value: _('None') },
                        { id: 'A', value: _('Activo') },
                        { id: 'C', value: _('Cancelado') },
                        { id: 'I', value: _('Pagada') },
                        { id: 'P', value: _('Aprobada') },
                        { id: 'R', value: _('Rechazada') },
                    ], // Dropdown options for state
                },
                {
                    id: 'CurrencyId', // Column ID
                    header: _('Moneda'), // Localized header
                    width: 100, // Column width
                    sort: 'int', // Enable integer sorting
                },
                {
                    id: 'Bets', // Column ID
                    header: _('Apuestas'), // Localized header
                    width: 100, // Column width
                    sort: 'int', // Enable integer sorting
                    format: webix.Number.numToStr({
                        groupDelimiter: ',', // Thousands separator
                        groupSize: 3, // Group size
                        decimalDelimiter: '.', // Decimal separator
                        decimalSize: 2, // Decimal places
                    }),
                    footer: { content: 'summTreeColumn' }, // Footer with sum
                    editor: 'text', // Enable text editing
                },
                {
                    id: 'Winnings', // Column ID
                    header: _('Premios'), // Localized header
                    width: 100, // Column width
                    sort: 'int', // Enable integer sorting
                    format: webix.Number.numToStr({
                        groupDelimiter: ',', // Thousands separator
                        groupSize: 3, // Group size
                        decimalDelimiter: '.', // Decimal separator
                        decimalSize: 2, // Decimal places
                    }),
                    footer: { content: 'summTreeColumn' }, // Footer with sum
                    editor: 'text', // Enable text editing
                },
                {
                    id: 'Bonus', // Column ID
                    header: _('Bonos'), // Localized header
                    width: 100, // Column width
                    sort: 'int', // Enable integer sorting
                    format: webix.Number.numToStr({
                        groupDelimiter: ',', // Thousands separator
                        groupSize: 3, // Group size
                        decimalDelimiter: '.', // Decimal separator
                        decimalSize: 2, // Decimal places
                    }),
                    footer: { content: 'summTreeColumn' }, // Footer with sum
                    editor: 'text', // Enable text editing
                },
                {
                    id: 'AmountBase', // Column ID
                    header: _('Valor Base'), // Localized header
                    width: 100, // Column width
                    sort: 'int', // Enable integer sorting
                    format: webix.Number.numToStr({
                        groupDelimiter: ',', // Thousands separator
                        groupSize: 3, // Group size
                        decimalDelimiter: '.', // Decimal separator
                        decimalSize: 2, // Decimal places
                    }),
                    footer: { content: 'summTreeColumn' }, // Footer with sum
                    editor: 'text', // Enable text editing
                },
                {
                    id: 'Amount', // Column ID
                    header: _('Comision'), // Localized header
                    width: 100, // Column width
                    sort: 'int', // Enable integer sorting
                    format: webix.Number.numToStr({
                        groupDelimiter: ',', // Thousands separator
                        groupSize: 3, // Group size
                        decimalDelimiter: '.', // Decimal separator
                        decimalSize: 2, // Decimal places
                    }),
                    footer: { content: 'summTreeColumn' }, // Footer with sum
                    editor: 'text', // Enable text editing
                },
                {
                    id: 'AmountPaid', // Column ID
                    header: _('Valor Pagado'), // Localized header
                    width: 100, // Column width
                    sort: 'int', // Enable integer sorting
                    format: webix.Number.numToStr({
                        groupDelimiter: ',', // Thousands separator
                        groupSize: 3, // Group size
                        decimalDelimiter: '.', // Decimal separator
                        decimalSize: 2, // Decimal places
                    }),
                    footer: { content: 'summTreeColumn' }, // Footer with sum
                    editor: 'text', // Enable text editing
                },
                {
                    id: 'ModifiedLocal', // Column ID
                    header: _('Modificado'), // Localized header
                    fillspace: 0, // No fill space
                    minWidth: 0, // Minimum width
                    width: 150, // Column width
                    sort: 'text', // Enable text sorting
                    format: dateFormat, // Date format
                },
            ],
            onClick: {
                /**
                 * Event handler for the "pay" action.
                 * @param {Object} euv - Event object.
                 * @param {string} id - ID of the clicked item.
                 */
                pay: function(euv, id) {
                    var vvthis = this;
                    webix
                        .modalbox({
                            title: _('Pregunta'),
                            buttons: [_('Saldo Afiliados'), _('Cancelar')],
                            text: _('Seleccione el tipo de saldo '),
                            width: 500,
                        })
                        .then(function(result) {
                            switch (result) {
                                case '0':
                                    webix.confirm({
                                        type: 'confirm-warning',
                                        text:
                                            _('Advertencia!') + '<br/>' + _('Desea abonar comisión a saldo afiliados?'),
                                        ok: _('Pagar Nota'),
                                        cancel: _('Salir'),
                                        callback: function(action) {
                                            if (action === true) {
                                                var item = vvthis.getItem(id);
                                                webix
                                                    .ajax()
                                                    .headers({
                                                        'Content-type': 'application/json',
                                                    })
                                                    .post(
                                                        devConfig.geturlApi() + 'Commisions/PayCommisions',
                                                        '{ "Id":' + item.Id + '}',
                                                        {
                                                            error: function(text, data, XmlHttpRequest) {
                                                                webix.message(_('Error! '));
                                                            },
                                                            success: function(text, data, XmlHttpRequest) {
                                                                item.State = 'P';
                                                                webix.message(_('Comisión Pagada'));
                                                                vthis.$$('table').clearAll();
                                                                vthis.$$('table').load(url);
                                                            },
                                                        },
                                                    );
                                            }
                                        },
                                    });
                                    break;
                                case '4':
                                    break;
                            }
                        });
                },
                /**
                 * Event handler for the "approve" action.
                 * @param {Object} euv - Event object.
                 * @param {string} id - ID of the clicked item.
                 */
                approve: function(euv, id) {
                    var vvthis = this;
                    webix.confirm({
                        type: 'confirm-warning',
                        text: _('Advertencia!') + '<br/>' + _('Desea Aprobar Comisión'),
                        ok: _('Aprobar Comisión'),
                        cancel: _('Salir'),
                        callback: function(action) {
                            if (action === true) {
                                var item = vvthis.getItem(id);
                                webix
                                    .ajax()
                                    .headers({
                                        'Content-type': 'application/json',
                                    })
                                    .post(
                                        devConfig.geturlApi() + 'Commisions/ApproveComission',
                                        '{ "Id":' + item.Id + '}',
                                        {
                                            error: function(text, data, XmlHttpRequest) {
                                                webix.message(_('Error! '));
                                            },
                                            success: function(text, data, XmlHttpRequest) {
                                                item.State = 'P';
                                                webix.message(_('Comisión Aprobado'));
                                                vthis.$$('table').clearAll();
                                                vthis.$$('table').load(url);
                                            },
                                        },
                                    );
                            }
                        },
                    });
                },
            },
            tooltip: true, // Enable tooltips
            footer: true, // Enable footer
            math: true, // Enable math operations
            loadahead: devConfig.getDefaultDataFetch(), // Preload data
            pager: 'pagerA', // Pager ID
            url: devConfig.geturlApi() + 'es/Financial/GetComissionsResume?FromId=' + cur_user + '&', // Data URL
        };
    }

    /**
     * Initializes the view and its components.
     * @param {Object} grid - The grid component to be initialized.
     */
    init(grid) {
        // Retrieve the localization service and the current user parameter
        const _ = this.app.getService('locale')._;
        const cur_user = this.getParam('user', true);
        // Define the URL for the grid data
        var url = grid.data.url;
        // Initialize an empty filter string
        var filter = '';
        // Attach an event handler for when the grid data is loaded
        grid.attachEvent('onAfterLoad', function() {
            // Group the data in the tree table by 'TypeName'
            $$('treetable').data.group({
                by: 'TypeName',
                map: {
                    AmountBase: ['AmountBase', 'sum'], // Sum of AmountBase
                    AmountPaid: ['AmountPaid', 'sum'], // Sum of AmountPaid
                    Amount: ['Amount', 'sum'], // Sum of Amount
                    CurrencyId: ['CurrencyId'], // CurrencyId
                },
            });
            // Group the data in the tree table by 'CurrencyId'
            $$('treetable').data.group({
                by: 'CurrencyId',
                map: {
                    AmountBase: ['AmountBase', 'sum'], // Sum of AmountBase
                    AmountPaid: ['AmountPaid', 'sum'], // Sum of AmountPaid
                    Amount: ['Amount', 'sum'], // Sum of Amount
                    CurrencyId: ['CurrencyId'], // CurrencyId
                },
            });
        });
        // Listen for the 'depositReport:filter' event to apply filters
        this.on(this.app, 'depositReport:filter', (filterData) => {
            // Convert filterData object into an array of entries
            var object = Object.entries(filterData);
            var string1 = '';
            // Iterate through each filter entry
            for (var i = 0; i < object.length; i++) {
                // Check if the value is not an object and not empty
                if (typeof object[i][1] !== 'object' && object[i][1] !== '') {
                    // Handle date range filtering
                    if (object[i][0] == 'Datepicker') {
                        var date = object[i][1].split(' - ');
                        string1 = string1 + '&dateFrom=' + date[0]; // Start date
                        string1 = string1 + '&dateTo=' + date[1]; // End date
                    } else {
                        // Append other filters to the string
                        string1 = string1 + '&' + object[i][0] + '=' + object[i][1];
                    }
                } else {
                    // Handle object type filters
                    if (typeof object[i][1] === 'object') {
                        var object2 = Object.entries(object[i][1]);
                        // Iterate through the nested filter object
                        for (var j = 0; j < object2.length; j++) {
                            if (object2[j][1] !== null) {
                                // Handle start date
                                if (object2[j][0] === 'start') {
                                    var d = new Date(object2[j][1]);
                                    var dateFrom =
                                        'dateFrom=' +
                                        d.getFullYear() +
                                        ':' +
                                        ('00' + (d.getMonth() + 1)).slice(-2) +
                                        ':' +
                                        ('00' + d.getDate()).slice(-2) +
                                        ' ' +
                                        ('00' + d.getHours()).slice(-2) +
                                        ':' +
                                        ('00' + d.getMinutes()).slice(-2) +
                                        ':' +
                                        ('00' + d.getSeconds()).slice(-2);
                                    string1 = string1 + '&' + dateFrom; // Append start date
                                } else {
                                    // Handle end date
                                    if (object2[j][0] === 'end') {
                                        var d = new Date(object2[j][1]);
                                        var dateTo =
                                            'dateTo=' +
                                            d.getFullYear() +
                                            ':' +
                                            ('00' + (d.getMonth() + 1)).slice(-2) +
                                            ':' +
                                            ('00' + d.getDate()).slice(-2) +
                                            ' ' +
                                            ('00' + d.getHours()).slice(-2) +
                                            ':' +
                                            ('00' + d.getMinutes()).slice(-2) +
                                            ':' +
                                            ('00' + d.getSeconds()).slice(-2);
                                        string1 = string1 + '&' + dateTo; // Append end date
                                    }
                                }
                            }
                        }
                    }
                }
            }
            // Append the current user ID to the filter string
            string1 = string1 + '&FromId=' + cur_user;
            // Clear the grid data
            grid.clearAll();
            // Load the grid data with the constructed URL and filters
            grid.load(url + '?start=0&count=' + grid.data.$pagesize + string1);
            // Update the filter variable
            filter = string1;
        });
        // Attach an event handler for sorting the grid
        grid.attachEvent('onAfterSort', function(by, dir, as) {
            // Load the grid data with sorting parameters
            grid.load(url + '?start=0&count=' + grid.data.$pagesize + filter + '&sort[' + by + ']=' + dir);
        });
        // Attach an event handler for item clicks in the grid
        grid.attachEvent('onItemClick', function(id, e, node) {
            var item = this.getItem(id); // Get the clicked item
            // Trigger an event with the item ID
            this.$scope.app.callEvent('deposit:id', [item.Id]);
        });
        // Listen for the 'player:select' event to load data based on selected player
        this.on(this.app, 'player:select', (Id) => {
            // Load the grid data for the selected player
            grid.load(grid.data.url + '?start=0&count=' + grid.data.$pagesize + '&FromId=' + user);
        }); // Placeholder for the initialization content
    }
}

// FILE DOCUMENTED
